import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Search, Plus, DollarSign, Copy, BookOpen, FileText, User, Calendar, Filter, Eye, Edit, Trash2, Save, X, Home, ShoppingBag, Wallet, Users, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ArrowLeft, LogOut, AlertTriangle, Loader } from 'lucide-react';

// COMPONENTE DE LOADING
const LoadingSpinner = ({ message = "Cargando..." }) => (
  <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
    <div className="bg-white rounded-lg p-6 shadow-xl flex flex-col items-center">
      <Loader className="animate-spin text-[#AD1457] mb-3" size={40} />
      <p className="text-gray-700 font-medium">{message}</p>
    </div>
  </div>
);

// MODAL DE CONFIRMACION
const ConfirmModal = ({ show, onClose, onConfirm, title, message, confirmText = "Confirmar", cancelText = "Cancelar", danger = false }) => {
  if (!show) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4 z-50" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start mb-4">
          <div className={`flex-shrink-0 ${danger ? 'text-red-600' : 'text-yellow-600'}`}>
            <AlertTriangle size={24} />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <p className="mt-2 text-sm text-gray-600">{message}</p>
          </div>
        </div>
        
        <div className="mt-6 flex justify-end space-x-3">
          <button 
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition"
          >
            {cancelText}
          </button>
          <button 
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`px-4 py-2 rounded text-white transition ${
              danger 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-[#C2185B] hover:bg-[#AD1457]'
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// MODAL CLIENTE
const AddClienteModal = ({ show, onClose, nuevoCliente, setNuevoCliente, onSave, isEditing }) => {
  if (!show) return null;

  const handleBackgroundMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4 z-50" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onMouseDown={handleBackgroundMouseDown}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-md" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{isEditing ? 'Editar Cliente' : 'Agregar Cliente'}</h2>
          <button onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre y Apellidos</label>
            <input
              type="text"
              value={nuevoCliente.nombre}
              onChange={(e) => setNuevoCliente({ ...nuevoCliente, nombre: e.target.value })}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseMove={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Ana López"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico</label>
            <input
              type="email"
              value={nuevoCliente.email}
              onChange={(e) => setNuevoCliente({ ...nuevoCliente, email: e.target.value })}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseMove={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="ana@gmail.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DNI</label>
            <input
              type="text"
              value={nuevoCliente.dni}
              onChange={(e) => setNuevoCliente({ ...nuevoCliente, dni: e.target.value.toUpperCase() })}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseMove={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="12345678A"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Saldo actual</label>
            <input
              type="number"
              step="0.01"
              value={nuevoCliente.saldo}
              onChange={(e) => setNuevoCliente({ ...nuevoCliente, saldo: parseFloat(e.target.value) || 0 })}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseMove={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              className="w-full border border-gray-300 rounded px-3 py-2"
              min="0"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={onSave} className="px-4 py-2 bg-[#C2185B] text-white rounded hover:bg-[#AD1457]">
            {isEditing ? 'Actualizar' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// MODAL SERVICIO
const AddServicioModal = ({ show, onClose, nuevoServicio, setNuevoServicio, onSave }) => {
  if (!show) return null;

  const handleBackgroundMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4 z-50" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onMouseDown={handleBackgroundMouseDown}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-md" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Agregar Servicio</h2>
          <button onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input
              type="text"
              value={nuevoServicio.nombre}
              onChange={(e) => setNuevoServicio({ ...nuevoServicio, nombre: e.target.value })}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Precio base</label>
            <input
              type="number"
              step="0.01"
              value={nuevoServicio.precio}
              onChange={(e) => setNuevoServicio({ ...nuevoServicio, precio: parseFloat(e.target.value) || 0 })}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="w-full border border-gray-300 rounded px-3 py-2"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={nuevoServicio.tipo}
              onChange={(e) => setNuevoServicio({ ...nuevoServicio, tipo: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="copias">Copias</option>
              <option value="encuadernacion">Encuadernación</option>
              <option value="impresion">Impresión</option>
              <option value="laminado">Laminado</option>
              <option value="otro">Otro</option>
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={onSave} className="px-4 py-2 bg-[#C2185B] text-white rounded hover:bg-[#AD1457]">
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

// MODAL EDITAR MOVIMIENTO
const EditMovimientoModal = ({ show, onClose, movimiento, clientes, servicios, onSave }) => {
  const [editedMovimiento, setEditedMovimiento] = useState(movimiento || {});

  // Actualizar estado cuando cambia el movimiento
  useEffect(() => {
    if (movimiento) {
      setEditedMovimiento(movimiento);
    }
  }, [movimiento]);
  
  // Recalcular monto cuando cambia servicio o cantidad
  useEffect(() => {
    if (editedMovimiento.servicioId && editedMovimiento.cantidad) {
      const servicio = servicios.find(s => s.id === editedMovimiento.servicioId);
      if (servicio) {
        const nuevoMonto = servicio.precio * editedMovimiento.cantidad;
        setEditedMovimiento(prev => ({ ...prev, monto: nuevoMonto }));
      }
    }
  }, [editedMovimiento.servicioId, editedMovimiento.cantidad, servicios]);

  if (!show || !movimiento) return null;

  const handleBackgroundMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const cliente = clientes.find(c => c.id === editedMovimiento.clienteId);
  const servicio = servicios.find(s => s.id === editedMovimiento.servicioId);

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4 z-50" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onMouseDown={handleBackgroundMouseDown}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-md" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Editar Movimiento</h2>
          <button onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <p className="font-bold text-gray-900">{cliente?.nombre || 'N/A'}</p>
              <p className="text-sm text-gray-600">{cliente?.dni || ''}</p>
            </div>
            <p className="text-xs text-gray-500 mt-1">El cliente no se puede cambiar</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Servicio</label>
            <select
              value={editedMovimiento.servicioId || ''}
              onChange={(e) => setEditedMovimiento({ ...editedMovimiento, servicioId: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              {servicios.map(servicio => (
                <option key={servicio.id} value={servicio.id}>{servicio.nombre}</option>
              ))}
            </select>
            {servicio && (
              <p className="text-xs text-gray-500 mt-1">Precio unitario: {servicio.precio.toFixed(2)}€</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
            <input
              type="number"
              value={editedMovimiento.cantidad || 1}
              onChange={(e) => setEditedMovimiento({ ...editedMovimiento, cantidad: parseInt(e.target.value) || 1 })}
              className="w-full border border-gray-300 rounded px-3 py-2"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monto Total</label>
            <div className="bg-[#FCE4EC] border border-[#F8BBD0] rounded p-3">
              <p className="text-2xl font-bold text-[#880E4F]">{editedMovimiento.monto?.toFixed(2) || '0.00'}€</p>
            </div>
            <p className="text-xs text-gray-500 mt-1">Calculado automáticamente: Precio x Cantidad</p>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={() => onSave(editedMovimiento)} className="px-4 py-2 bg-[#C2185B] text-white rounded hover:bg-[#AD1457]">
            Guardar Cambios
          </button>
        </div>
      </div>
    </div>
  );
};

// MODAL AGREGAR GASTO
const AddGastoModal = ({ show, onClose, clientes, servicios, selectedCliente, setSelectedCliente, selectedServicio, setSelectedServicio, cantidad, setCantidad, onSave, clientePreseleccionado }) => {
  const [searchCliente, setSearchCliente] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [ultimoGasto, setUltimoGasto] = useState(null);

  // Reset summary when modal closes
  useEffect(() => {
    if (!show) {
      setUltimoGasto(null);
    }
  }, [show]);

  if (!show) return null;

  const filteredClientes = clientes.filter(cliente =>
    cliente.nombre.toLowerCase().includes(searchCliente.toLowerCase()) ||
    cliente.dni.toLowerCase().includes(searchCliente.toLowerCase())
  );

  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente.id);
    setSearchCliente(cliente.nombre);
    setShowClienteDropdown(false);
  };

  const clienteActual = clientePreseleccionado || clientes.find(c => c.id === selectedCliente);

  const handleBackgroundMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4 z-50" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onMouseDown={handleBackgroundMouseDown}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-md" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Agregar Gasto</h2>
          <button onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          {clientePreseleccionado ? (
            <div className="bg-[#FCE4EC] border border-[#F8BBD0] rounded p-3">
              <p className="text-sm font-medium text-gray-700">Cliente:</p>
              <p className="font-bold text-gray-900">{clientePreseleccionado.nombre}</p>
              <p className="text-sm text-gray-600">{clientePreseleccionado.dni}</p>
              <div className="mt-2 pt-2 border-t border-[#F8BBD0]">
                <p className="text-xs text-gray-600">Saldo actual:</p>
                <p className={`text-lg font-bold ${clientePreseleccionado.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {clientePreseleccionado.saldo.toFixed(2)}€
                </p>
              </div>
            </div>
          ) : (
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
              <input
                type="text"
                value={searchCliente}
                onChange={(e) => {
                  setSearchCliente(e.target.value);
                  setShowClienteDropdown(true);
                  setSelectedCliente(null);
                }}
                onFocus={() => setShowClienteDropdown(true)}
                placeholder="Buscar cliente por nombre o DNI..."
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
              {showClienteDropdown && searchCliente && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                  {filteredClientes.length > 0 ? (
                    filteredClientes.map(cliente => (
                      <div
                        key={cliente.id}
                        onClick={() => handleSelectCliente(cliente)}
                        className="px-3 py-2 hover:bg-[#FCE4EC] cursor-pointer border-b last:border-b-0"
                      >
                        <div className="font-medium">{cliente.nombre}</div>
                        <div className="text-sm text-gray-500">{cliente.dni}</div>
                        <div className={`text-xs font-semibold mt-1 ${cliente.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          Saldo: {cliente.saldo.toFixed(2)}€
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-gray-500">No se encontraron clientes</div>
                  )}
                </div>
              )}
              
              {/* Mostrar saldo del cliente seleccionado */}
              {clienteActual && !clientePreseleccionado && (
                <div className="mt-2 bg-gray-50 border border-gray-200 rounded p-2">
                  <p className="text-xs text-gray-600">Saldo actual de {clienteActual.nombre}:</p>
                  <p className={`text-lg font-bold ${clienteActual.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {clienteActual.saldo.toFixed(2)}€
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Servicio</label>
            <select
              value={selectedServicio || ''}
              onChange={(e) => setSelectedServicio(parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="">Seleccionar servicio</option>
              {servicios.map(servicio => (
                <option key={servicio.id} value={servicio.id}>{servicio.nombre} - {servicio.precio}€</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
            <input
              type="number"
              value={cantidad}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || val === '0') {
                  setCantidad('');
                } else {
                  setCantidad(parseInt(val) || '');
                }
              }}
              onBlur={(e) => {
                if (cantidad === '' || cantidad === 0) {
                  setCantidad(1);
                }
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseMove={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onSelect={(e) => e.stopPropagation()}
              onDrag={(e) => e.stopPropagation()}
              onDragStart={(e) => e.stopPropagation()}
              className="w-full border border-gray-300 rounded px-3 py-2"
              min="1"
            />
          </div>
        </div>

        {/* Resumen del último gasto guardado */}
        {ultimoGasto && (
          <div className="mt-4 p-4 bg-[#FCE4EC] border border-[#F8BBD0] rounded">
            <p className="font-bold text-[#880E4F] mb-2">
              ✓ {ultimoGasto.esRecarga ? 'Recarga' : 'Gasto'} guardado correctamente
            </p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700">{ultimoGasto.esRecarga ? 'Recarga:' : 'Gasto:'}</span>
                <span className={`font-bold ${ultimoGasto.esRecarga ? 'text-green-600' : 'text-red-600'}`}>
                  {ultimoGasto.esRecarga ? '+' : '-'}{ultimoGasto.monto.toFixed(2)}€
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Saldo actual:</span>
                <span className={`font-bold ${ultimoGasto.saldoActual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {ultimoGasto.saldoActual.toFixed(2)}€
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-3">
          <button onClick={() => {
            setUltimoGasto(null);
            onClose();
          }} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
            {ultimoGasto ? 'Cerrar' : 'Cancelar'}
          </button>
          <button onClick={async () => {
            const result = await onSave();
            if (result && result.success) {
              setUltimoGasto({
                monto: result.monto,
                saldoActual: result.saldoActual,
                esRecarga: result.esRecarga
              });
            }
          }} className="px-4 py-2 bg-[#C2185B] text-white rounded hover:bg-[#AD1457]">
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

// MODAL VER MOVIMIENTOS DE CLIENTE
const ViewClienteMovimientosModal = ({ show, onClose, cliente, movimientos, servicios }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  if (!show || !cliente) return null;

  const clienteMovimientos = movimientos.filter(m => m.clienteId === cliente.id);
  const totalPages = Math.ceil(clienteMovimientos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMovimientos = clienteMovimientos.slice(startIndex, endIndex);

  const handleBackgroundMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4 z-50" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onMouseDown={handleBackgroundMouseDown}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold">Movimientos de {cliente.nombre}</h2>
            <p className="text-sm text-gray-600">DNI: {cliente.dni} | Saldo actual: {cliente.saldo.toFixed(2)}€</p>
          </div>
          <button onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {clienteMovimientos.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No hay movimientos para este cliente</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedMovimientos.map((mov) => {
                    const servicio = servicios.find(s => s.id === mov.servicioId);
                    const esRecarga = servicio?.nombre?.toLowerCase().includes('recarga');
                    return (
                      <tr key={mov.id}>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                          {mov.fecha || 'Sin fecha'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                          {servicio?.nombre || 'N/A'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                          {mov.cantidad}
                        </td>
                        <td className={`px-3 py-3 whitespace-nowrap text-sm ${esRecarga ? 'text-green-600' : 'text-red-600'}`}>
                          {esRecarga ? '+' : '-'}{mov.monto.toFixed(2)}€
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-center items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// PANTALLA DE LOGIN ADMIN
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('https://bono.satigijon.com/api.php?action=auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });
      
      const data = await response.json();
      
      if (data.success) {
        onLogin('admin', rememberMe);
      } else {
        // Mostrar el mensaje del servidor (incluye rate limiting)
        setError(data.error || 'Usuario o contraseña incorrectos');
      }
    } catch (error) {
      console.error('Error de autenticación:', error);
      setError('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#C2185B] to-[#880E4F] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img 
            src="/logo.png" 
            alt="I-Gijón" 
            className="w-32 h-32"
          />
        </div>
        <h2 className="text-2xl font-bold text-center mb-2">Acceso Administrador</h2>
        <p className="text-gray-600 text-center mb-6" style={{ fontFamily: 'Georgia, serif', fontSize: '1.25rem' }}>I-Gijón S.A.T.</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
            <input
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setError('');
              }}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#AD1457]"
              placeholder="Nombre de usuario"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#AD1457]"
              placeholder="Contraseña"
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-[#AD1457] border-gray-300 rounded"
            />
            <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
              Recordar en este dispositivo
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#C2185B] text-white py-2 rounded hover:bg-[#880E4F] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Verificando...' : 'Ingresar'}
          </button>
        </form>
      </div>
    </div>
  );
};

// PANTALLA DE LOGIN CLIENTE
const ClienteLogin = ({ onLogin, clientes }) => {
  const [email, setEmail] = useState('');
  const [dni, setDni] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!email.trim() || !dni.trim()) {
      setError('Por favor complete todos los campos');
      return;
    }
    
    // Buscar cliente con comparación más flexible
    const emailBuscado = email.toLowerCase().trim();
    const dniBuscado = dni.toUpperCase().trim();
    
    console.log('Buscando cliente con:', { emailBuscado, dniBuscado });
    
    const cliente = clientes.find(c => {
      const emailDB = (c.email || '').toLowerCase().trim();
      const dniDB = (c.dni || '').toUpperCase().trim();
      
      console.log('Comparando con:', { 
        nombre: c.nombre,
        emailDB, 
        dniDB,
        emailMatch: emailDB === emailBuscado,
        dniMatch: dniDB === dniBuscado
      });
      
      return emailDB === emailBuscado && dniDB === dniBuscado;
    });
    
    console.log('Cliente encontrado:', cliente);
    
    if (cliente) {
      onLogin('cliente', false, cliente);
    } else {
      setError('Email o DNI incorrectos');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#C2185B] to-[#880E4F] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img 
            src="/logo.png" 
            alt="I-Gijón" 
            className="w-32 h-32"
          />
        </div>
        <h2 className="text-2xl font-bold text-center mb-2">Consulta tu Saldo</h2>
        <p className="text-gray-600 text-center mb-6" style={{ fontFamily: 'Georgia, serif', fontSize: '1.25rem' }}>I-Gijón S.A.T.</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Correo Electrónico</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#AD1457]"
              placeholder="ejemplo@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DNI / Contraseña</label>
            <input
              type="text"
              value={dni}
              onChange={(e) => {
                setDni(e.target.value.toUpperCase());
                setError('');
              }}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#AD1457]"
              placeholder="12345678A"
              maxLength="9"
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}

          <button
            type="submit"
            className="w-full bg-[#C2185B] text-white py-2 rounded hover:bg-[#880E4F] transition"
          >
            Consultar
          </button>
        </form>
      </div>
    </div>
  );
};

// VISTA CLIENTE (Solo lectura)
const ClienteView = ({ cliente, movimientos, servicios, onLogout }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalContent, setLegalContent] = useState('');
  const itemsPerPage = 25;

  const clienteMovimientos = movimientos.filter(m => m.clienteId === cliente.id);
  const totalPages = Math.ceil(clienteMovimientos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMovimientos = clienteMovimientos.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header style={{ background: 'linear-gradient(135deg, #C2185B 0%, #880E4F 100%)' }} className="text-white shadow-lg">
        <div className="container mx-auto px-3 md:px-4 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 md:space-x-4">
              <img 
                src="/logo.png" 
                alt="I-Gijón" 
                className="w-10 h-10 md:w-12 md:h-12"
              />
              <h1 className="text-base md:text-xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>I-Gijón S.A.T.</h1>
            </div>
            <button 
              onClick={onLogout}
              className="px-3 md:px-4 py-2 rounded transition flex items-center gap-1 md:gap-2 text-sm md:text-base"
              style={{ background: 'rgba(255,255,255,0.2)' }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              <LogOut size={16} className="md:hidden" />
              <LogOut size={18} className="hidden md:block" />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-4 md:py-6">
        <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold mb-4">Bienvenido/a, {cliente.nombre}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <div className="bg-gray-50 p-3 md:p-4 rounded">
              <p className="text-xs md:text-sm text-gray-600">DNI</p>
              <p className="text-base md:text-lg font-semibold break-all">{cliente.dni}</p>
            </div>
            <div className="bg-gray-50 p-3 md:p-4 rounded">
              <p className="text-xs md:text-sm text-gray-600">Email</p>
              <p className="text-base md:text-lg font-semibold break-all">{cliente.email}</p>
            </div>
            <div className="bg-gray-50 p-3 md:p-4 rounded">
              <p className="text-xs md:text-sm text-gray-600">Saldo Actual</p>
              <p className={`text-3xl md:text-2xl font-bold ${cliente.saldo < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {cliente.saldo.toFixed(2)}€
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h3 className="text-lg md:text-xl font-bold mb-4">Mis Movimientos</h3>
          
          {clienteMovimientos.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No tienes movimientos registrados</p>
          ) : (
            <>
              {/* Vista de escritorio (tabla) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedMovimientos.map((mov) => {
                      const servicio = servicios.find(s => s.id === mov.servicioId);
                      const esRecarga = servicio?.nombre?.toLowerCase().includes('recarga');
                      return (
                        <tr key={mov.id}>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                            {mov.fecha === 'Invalid Date' || !mov.fecha ? 'Sin fecha' : mov.fecha}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                            {servicio?.nombre || 'N/A'}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                            {mov.cantidad}
                          </td>
                          <td className={`px-3 py-3 whitespace-nowrap text-sm font-semibold ${esRecarga ? 'text-green-600' : 'text-red-600'}`}>
                            {esRecarga ? '+' : '-'}{mov.monto.toFixed(2)}€
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Vista móvil (tarjetas) */}
              <div className="md:hidden space-y-3">
                {paginatedMovimientos.map((mov) => {
                  const servicio = servicios.find(s => s.id === mov.servicioId);
                  const esRecarga = servicio?.nombre?.toLowerCase().includes('recarga');
                  return (
                    <div key={mov.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 text-base">{servicio?.nombre || 'N/A'}</p>
                          <p className="text-sm text-gray-500">{mov.fecha === 'Invalid Date' || !mov.fecha ? 'Sin fecha' : mov.fecha}</p>
                        </div>
                        <div className={`text-right ml-3 ${esRecarga ? 'text-green-600' : 'text-red-600'}`}>
                          <p className="text-xl font-bold">{esRecarga ? '+' : '-'}{mov.monto.toFixed(2)}€</p>
                        </div>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600 pt-2 border-t border-gray-100">
                        <span>Cantidad:</span>
                        <span className="font-medium">{mov.cantidad}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex justify-center items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 text-sm md:text-base"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <span className="text-sm md:text-base px-2">
                    <span className="hidden md:inline">Página </span>{currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 text-sm md:text-base"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer Legal */}
      <footer className="bg-gray-800 text-gray-300 py-6 mt-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm mb-2">© 2025 I-Gijón S.A.T. - Todos los derechos reservados</p>
          <div className="flex justify-center space-x-4 text-sm">
            <button 
              onClick={() => {
                setLegalContent('aviso');
                setShowLegalModal(true);
              }}
              className="hover:text-white underline"
            >
              Aviso Legal
            </button>
            <span>|</span>
            <button 
              onClick={() => {
                setLegalContent('privacidad');
                setShowLegalModal(true);
              }}
              className="hover:text-white underline"
            >
              Política de Privacidad
            </button>
          </div>
          <p className="text-xs mt-2 text-gray-400">
            C/ Brasil 19 Bajo, 33213 Gijón (Asturias) | satigijon@gmail.com
          </p>
        </div>
      </footer>

      {/* Modal Legal */}
      {showLegalModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowLegalModal(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">
                {legalContent === 'aviso' ? 'Aviso Legal' : 'Política de Privacidad'}
              </h2>
              <button onClick={() => setShowLegalModal(false)}>
                <X size={24} />
              </button>
            </div>

            {legalContent === 'aviso' ? (
              <div className="prose max-w-none text-sm">
                <p className="font-bold mb-4">AVISO LEGAL</p>
                <p className="mb-4">En cumplimiento de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), I-Gijón S.A.T. informa a los usuarios del sitio web https://bono.satigijon.com de los siguientes datos:</p>
                
                <p className="font-bold mt-4 mb-2">DATOS IDENTIFICATIVOS</p>
                <p>Titular: I-Gijón S.A.T.<br/>
                CIF: 71438691R<br/>
                Domicilio social: C/ Brasil 19 Bajo, 33213 Gijón (Asturias), España<br/>
                Correo electrónico: satigijon@gmail.com</p>

                <p className="font-bold mt-4 mb-2">OBJETO</p>
                <p>El presente sitio web tiene por objeto facilitar a los clientes de I-Gijón S.A.T. la consulta de su saldo de bonos y el historial de movimientos asociados a los servicios contratados.</p>

                <p className="font-bold mt-4 mb-2">CONDICIONES DE USO</p>
                <p>El acceso y uso de este sitio web implica la aceptación de las presentes condiciones. El usuario se compromete a utilizar el sitio de forma lícita, no realizar actividades que puedan dañarlo y mantener la confidencialidad de sus credenciales.</p>

                <p className="font-bold mt-4 mb-2">PROPIEDAD INTELECTUAL</p>
                <p>Todos los contenidos son propiedad de I-Gijón S.A.T. y están protegidos por derechos de propiedad intelectual e industrial.</p>

                <p className="font-bold mt-4 mb-2">LEGISLACIÍ“N APLICABLE</p>
                <p>Las presentes condiciones se rigen por la legislación española. Para cualquier controversia, las partes se someten a los Juzgados y Tribunales de Gijón (Asturias).</p>
              </div>
            ) : (
              <div className="prose max-w-none text-sm">
                <p className="font-bold mb-4">POLÍTICA DE PRIVACIDAD</p>
                <p className="mb-4">En cumplimiento del RGPD y la LOPDGDD, I-Gijón S.A.T. informa sobre el tratamiento de datos personales.</p>

                <p className="font-bold mt-4 mb-2">1. RESPONSABLE DEL TRATAMIENTO</p>
                <p>I-Gijón S.A.T. | CIF: 71438691R<br/>
                C/ Brasil 19 Bajo, 33213 Gijón (Asturias)<br/>
                satigijon@gmail.com</p>

                <p className="font-bold mt-4 mb-2">2. DATOS QUE RECOGEMOS</p>
                <p>Nombre, apellidos, DNI, correo electrónico, teléfono e historial de servicios contratados.</p>

                <p className="font-bold mt-4 mb-2">3. FINALIDAD</p>
                <p>Gestión de bonos, facturación, control de saldos y cumplimiento de obligaciones legales.</p>

                <p className="font-bold mt-4 mb-2">4. CONSERVACIÍ“N</p>
                <p>Los datos se conservan durante la relación comercial y posteriormente durante los plazos legales (mínimo 6 años).</p>

                <p className="font-bold mt-4 mb-2">5. SUS DERECHOS</p>
                <p>Tiene derecho a acceder, rectificar, suprimir, limitar, portar y oponerse al tratamiento de sus datos. Puede ejercerlos escribiendo a satigijon@gmail.com acreditando su identidad.</p>

                <p className="font-bold mt-4 mb-2">6. RECLAMACIONES</p>
                <p>Puede presentar reclamaciones ante la Agencia Española de Protección de Datos (www.aepd.es).</p>

                <p className="font-bold mt-4 mb-2">7. SEGURIDAD</p>
                <p>Hemos adoptado medidas técnicas apropiadas para proteger sus datos frente a accesos no autorizados. El acceso requiere autenticación mediante email y DNI.</p>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowLegalModal(false)}
                className="bg-[#C2185B] text-white px-6 py-2 rounded hover:bg-[#880E4F]"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// COMPONENTE PRINCIPAL
const App = () => {
  // Detectar si es admin desde parámetro URL (?admin)
  const [isAdminRoute] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('admin');
  });
  
  // Estados de autenticación
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null); // 'admin' o 'cliente'
  const [currentCliente, setCurrentCliente] = useState(null);

  // API URL - Cambiar según el entorno
  const API_URL = 'https://bono.satigijon.com/api.php';
  
  // Estados existentes
  const [activeTab, setActiveTab] = useState(sessionStorage.getItem('activeTab') || 'resumen');
  
  // Guardar la pestaña activa en sessionStorage cuando cambie
  useEffect(() => {
    sessionStorage.setItem('activeTab', activeTab);
  }, [activeTab]);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentClientesPage, setCurrentClientesPage] = useState(1);
  const clientesPerPage = 25;
  const [searchMovimientos, setSearchMovimientos] = useState('');
  
  // Estados para filtros avanzados de movimientos
  const [filtroServicio, setFiltroServicio] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos'); // todos, gastos, recargas
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [filtroMontoMin, setFiltroMontoMin] = useState('');
  const [filtroMontoMax, setFiltroMontoMax] = useState('');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  
  const [showAddCliente, setShowAddCliente] = useState(false);
  const [showAddServicio, setShowAddServicio] = useState(false);
  const [showAddGasto, setShowAddGasto] = useState(false);
  const [showEditMovimiento, setShowEditMovimiento] = useState(false);
  const [showViewMovimientos, setShowViewMovimientos] = useState(false);
  const [viewingCliente, setViewingCliente] = useState(null);
  const [editingMovimiento, setEditingMovimiento] = useState(null);
  const [editingClienteId, setEditingClienteId] = useState(null);
  
  // Estados para confirmación de borrado
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'movimiento', id: 123 }
  
  // Estados para mostrar/ocultar datos sensibles en panel general
  const [showDeudaClientes, setShowDeudaClientes] = useState(false);
  const [showDeudaTotal, setShowDeudaTotal] = useState(false);
  const [showSaldoPositivo, setShowSaldoPositivo] = useState(false);
  const [showListaDeuda, setShowListaDeuda] = useState(false);
  const [currentDeudaPage, setCurrentDeudaPage] = useState(1);
  
  // Estado para orden de servicios
  const [serviciosOrden, setServiciosOrden] = useState([]);
  
  // Estado de carga
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false); // Para acciones como guardar, borrar, etc.
  const [loadingMessage, setLoadingMessage] = useState('');
  
  const [nuevoCliente, setNuevoCliente] = useState({
    nombre: '',
    email: '',
    dni: '',
    saldo: 0
  });

  const [nuevoServicio, setNuevoServicio] = useState({
    nombre: '',
    precio: 0,
    tipo: 'copias'
  });

  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedServicio, setSelectedServicio] = useState(null);
  const [cantidad, setCantidad] = useState(1);

  const [clientes, setClientes] = useState([]);

  const [servicios, setServicios] = useState([]);

  const [movimientos, setMovimientos] = useState([]);

  // Cargar datos desde la API al montar el componente
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        
        // Cargar clientes
        const resClientes = await fetch(`${API_URL}?action=clientes`);
        const dataClientes = await resClientes.json();
        if (dataClientes.success) {
          setClientes(dataClientes.data);
        }
        
        // Cargar servicios
        const resServicios = await fetch(`${API_URL}?action=servicios`);
        const dataServicios = await resServicios.json();
        if (dataServicios.success) {
          setServicios(dataServicios.data);
        }
        
        // Cargar movimientos
        const resMovimientos = await fetch(`${API_URL}?action=movimientos`);
        const dataMovimientos = await resMovimientos.json();
        if (dataMovimientos.success) {
          setMovimientos(dataMovimientos.data);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error cargando datos:', err);
        setError('Error al cargar los datos');
        setLoading(false);
      }
    };
    
    // Cargar datos siempre (necesarios para login de clientes también)
    cargarDatos();
  }, [API_URL]);

  
  // Funciones para recargar datos individuales (llamadas al cambiar de pestaña)
  const recargarClientes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}?action=clientes`);
      const data = await res.json();
      if (data.success) {
        setClientes(data.data);
      }
    } catch (err) {
      console.error('Error recargando clientes:', err);
    }
  }, [API_URL]);
  
  const recargarServicios = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}?action=servicios`);
      const data = await res.json();
      if (data.success) {
        setServicios(data.data);
      }
    } catch (err) {
      console.error('Error recargando servicios:', err);
    }
  }, [API_URL]);
  
  const recargarMovimientos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}?action=movimientos`);
      const data = await res.json();
      if (data.success) {
        setMovimientos(data.data);
      }
    } catch (err) {
      console.error('Error recargando movimientos:', err);
    }
  }, [API_URL]);
  
  // Recargar datos cuando cambias de pestaña
  useEffect(() => {
    if (activeTab === 'resumen') {
      // General necesita clientes actualizados
      recargarClientes();
    } else if (activeTab === 'clientes') {
      // Pestaña clientes
      recargarClientes();
    } else if (activeTab === 'movimientos') {
      // Pestaña movimientos necesita clientes, servicios y movimientos
      recargarClientes();
      recargarServicios();
      recargarMovimientos();
    } else if (activeTab === 'servicios') {
      // Pestaña servicios
      recargarServicios();
    }
  }, [activeTab, recargarClientes, recargarServicios, recargarMovimientos]);
  // Inicializar orden de servicios
  useEffect(() => {
    if (serviciosOrden.length === 0 && servicios.length > 0) {
      setServiciosOrden(servicios.map(s => s.id));
    }
  }, [servicios, serviciosOrden.length]);


  // Paginación para movimientos
  const [currentMovimientosPage, setCurrentMovimientosPage] = useState(1);
  const movimientosPerPage = 25;

  // Cargar autenticación guardada
  useEffect(() => {
    const savedAuth = localStorage.getItem('adminAuth');
    // Solo restaurar sesión de admin si estamos en la ruta admin
    if (savedAuth === 'true' && isAdminRoute) {
      setIsAuthenticated(true);
      setUserType('admin');
    }
    
    // Restaurar sesión de cliente si no es ruta admin
    if (!isAdminRoute) {
      const savedClienteId = sessionStorage.getItem('clienteId');
      if (savedClienteId) {
        setIsAuthenticated(true);
        setUserType('cliente');
        // El cliente se cargará cuando lleguen los datos de la API
      }
    }
  }, [isAdminRoute]);

  // Cargar/actualizar datos del cliente cuando llegan los clientes de la API
  useEffect(() => {
    if (!isAdminRoute && clientes.length > 0) {
      const savedClienteId = sessionStorage.getItem('clienteId');
      if (savedClienteId) {
        const clienteEncontrado = clientes.find(c => c.id === parseInt(savedClienteId));
        if (clienteEncontrado) {
          setCurrentCliente(clienteEncontrado);
        } else {
          // Cliente no encontrado, cerrar sesión
          sessionStorage.removeItem('clienteId');
          setIsAuthenticated(false);
          setUserType(null);
          setCurrentCliente(null);
        }
      }
    }
  }, [clientes, isAdminRoute]);

  const handleLogin = (type, remember, cliente = null) => {
    setIsAuthenticated(true);
    setUserType(type);
    if (type === 'admin' && remember) {
      localStorage.setItem('adminAuth', 'true');
    }
    if (type === 'cliente') {
      setCurrentCliente(cliente);
      // Guardar solo el ID del cliente en sessionStorage
      sessionStorage.setItem('clienteId', cliente.id.toString());
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserType(null);
    setCurrentCliente(null);
    localStorage.removeItem('adminAuth');
    // Limpiar sesión del cliente
    sessionStorage.removeItem('clienteId');
  };

  // Funciones existentes
  const handleAddCliente = useCallback(async () => {
    if (!nuevoCliente.nombre || !nuevoCliente.dni) {
      alert('Por favor complete todos los campos obligatorios');
      return;
    }

    try {
      if (editingClienteId !== null) {
        // EDITAR cliente existente
        const response = await fetch(`${API_URL}?action=update_cliente`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: editingClienteId,
            nombre: nuevoCliente.nombre,
            email: nuevoCliente.email,
            dni: nuevoCliente.dni,
            saldo: nuevoCliente.saldo
          })
        });

        const data = await response.json();

        if (data.success) {
          // Actualizar estado local
          setClientes(prev => prev.map(c => 
            c.id === editingClienteId 
              ? { ...c, ...nuevoCliente }
              : c
          ));
          setEditingClienteId(null);
        } else {
          alert('Error al actualizar: ' + (data.error || 'Error desconocido'));
          return;
        }
      } else {
      // AGREGAR nuevo cliente - CON LLAMADA AL API
      const response = await fetch(`${API_URL}?action=add_cliente`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: nuevoCliente.nombre,
          email: nuevoCliente.email,
          dni: nuevoCliente.dni,
          saldo: nuevoCliente.saldo
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Agregar al estado local con el ID retornado por el servidor
        const clienteConId = {
          ...nuevoCliente,
          id: data.id || data.cliente?.id
        };
        setClientes(prev => [...prev, clienteConId]);
      } else {
        alert('Error al crear cliente: ' + (data.error || 'Error desconocido'));
        return;
      }
    }

      setShowAddCliente(false);
      setNuevoCliente({ nombre: '', email: '', dni: '', saldo: 0 });
    } catch (error) {
      console.error('Error:', error);
      alert('Error al conectar con el servidor');
    }
  }, [nuevoCliente, clientes, editingClienteId, API_URL]);

  const handleEditCliente = (id) => {
    const cliente = clientes.find(c => c.id === id);
    if (cliente) {
      setNuevoCliente(cliente);
      setEditingClienteId(id);
      setShowAddCliente(true);
    }
  };

  const handleDeleteCliente = async (id) => {
  if (window.confirm('¿Está seguro de eliminar este cliente?')) {
    try {
      const response = await fetch(`${API_URL}?action=cliente&id=${id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setClientes(prev => prev.filter(c => c.id !== id));
        setMovimientos(prev => prev.filter(m => m.clienteId !== id));
      } else {
        alert('Error al eliminar el cliente');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al conectar con el servidor');
    }
  }
};

  const handleAddServicio = useCallback(async () => {
    if (!nuevoServicio.nombre || nuevoServicio.precio <= 0) {
      alert('Por favor complete todos los campos correctamente');
      return;
    }

    try {
      const response = await fetch(`${API_URL}?action=add_servicio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: nuevoServicio.nombre,
          precio: nuevoServicio.precio,
          tipo: nuevoServicio.tipo
        })
      });

      const data = await response.json();

      if (data.success) {
        const servicioConId = {
          ...nuevoServicio,
          id: data.id || data.servicio?.id
        };
        setServicios(prev => [...prev, servicioConId]);
        setShowAddServicio(false);
        setNuevoServicio({ nombre: '', precio: 0, tipo: 'copias' });
      } else {
        alert('Error al crear servicio: ' + (data.error || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al conectar con el servidor');
    }
  }, [nuevoServicio, API_URL]);
  const handleDeleteServicio = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este servicio?')) {
      try {
        const response = await fetch(`${API_URL}?action=servicio&id=${id}`, {
          method: 'DELETE'
        });
        
        const data = await response.json();
        
        if (data.success) {
          // Eliminar del estado local
          setServicios(prev => prev.filter(s => s.id !== id));
          setServiciosOrden(prev => prev.filter(sId => sId !== id));
        } else {
          alert('Error al eliminar: ' + (data.error || 'Error desconocido'));
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error al conectar con el servidor');
      }
    }
  };

  const handleAddGasto = useCallback(async () => {
    if (!selectedCliente || !selectedServicio) {
      alert('Por favor seleccione un cliente y un servicio');
      return { success: false };
    }

    const cantidadFinal = cantidad === '' || cantidad === 0 ? 1 : cantidad;
    const servicio = servicios.find(s => s.id === selectedServicio);
    
    if (!servicio) {
      alert('Servicio no encontrado');
      return { success: false };
    }
    
    const monto = servicio.precio * cantidadFinal;

    try {
      // Enviar a la API
      const response = await fetch(`${API_URL}?action=add_movimiento`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clienteId: selectedCliente,
          servicioId: selectedServicio,
          cantidad: cantidadFinal,
          monto: monto
        })
      });

      const data = await response.json();

      if (data.success) {
        // Actualizar estado local con el ID retornado
        const newMovimiento = {
          id: data.id,
          clienteId: selectedCliente,
          servicioId: selectedServicio,
          cantidad: cantidadFinal,
          monto: monto,
          fecha: data.fecha
        };

        setMovimientos(prev => [...prev, newMovimiento]);
        
        // Obtener nuevo saldo del cliente
        let nuevoSaldo = 0;
        const clienteEncontrado = clientes.find(c => c.id === selectedCliente);
        
        if (clienteEncontrado) {
          // Detectar si es recarga (suma) o gasto (resta)
          const esRecarga = servicio.nombre.toLowerCase().includes('recarga');
          nuevoSaldo = esRecarga 
            ? clienteEncontrado.saldo + monto  // Recargas SUMAN
            : clienteEncontrado.saldo - monto; // Gastos RESTAN
          
          setClientes(prev => prev.map(c => 
            c.id === selectedCliente 
              ? { ...c, saldo: nuevoSaldo }
              : c
          ));
        }

        // NO cerrar el modal, solo resetear servicio y cantidad
        setSelectedServicio(null);
        setCantidad(1);
        
        // Detectar si es recarga
        const esRecarga = servicio.nombre.toLowerCase().includes('recarga');
        
        // Retornar datos para mostrar en el resumen
        return {
          success: true,
          monto: monto,
          saldoActual: nuevoSaldo,
          esRecarga: esRecarga
        };
      } else {
        alert('Error al guardar: ' + (data.error || 'Error desconocido'));
        return { success: false };
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al conectar con el servidor');
      return { success: false };
    }
  }, [selectedCliente, selectedServicio, cantidad, servicios, clientes, API_URL]);

  const handleAddGastoFromCliente = (clienteId) => {
    setSelectedCliente(clienteId);
    
    // Buscar el servicio "Copias B/N" para preseleccionarlo
    const copiasBN = servicios.find(s => s.nombre.toLowerCase().includes('copias b/n'));
    if (copiasBN) {
      setSelectedServicio(copiasBN.id);
    }
    
    setShowAddGasto(true);
  };

  const handleEditMovimiento = (movimiento) => {
    setEditingMovimiento(movimiento);
    setShowEditMovimiento(true);
  };

  const handleSaveEditMovimiento = async (editedMov) => {
    setActionLoading(true);
    setLoadingMessage('Guardando cambios...');
    
    try {
      // Calcular el monto basado en servicio y cantidad
      const servicio = servicios.find(s => s.id === editedMov.servicioId);
      if (!servicio) {
        setActionLoading(false);
        alert('Servicio no encontrado');
        return;
      }
      
      const montoCalculado = servicio.precio * editedMov.cantidad;
      
      // Llamar al API
      const response = await fetch(`${API_URL}?action=update_movimiento`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editedMov.id,
          servicioId: editedMov.servicioId,
          cantidad: editedMov.cantidad,
          monto: montoCalculado
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Recargar página para actualizar todo
        sessionStorage.setItem('activeTab', 'movimientos');
        window.location.reload();
      } else {
        setActionLoading(false);
        alert('Error al actualizar: ' + (data.error || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error:', error);
      setActionLoading(false);
      alert('Error al conectar con el servidor');
    }
  };

  // Mostrar modal de confirmación para borrar movimiento
  const handleDeleteMovimientoClick = (id) => {
    setDeleteTarget({ type: 'movimiento', id });
    setShowConfirmDelete(true);
  };

  // Ejecutar borrado después de confirmar
  const handleDeleteMovimiento = async () => {
    if (!deleteTarget || deleteTarget.type !== 'movimiento') return;
    
    setActionLoading(true);
    setLoadingMessage('Eliminando movimiento...');
    
    try {
const response = await fetch(`${API_URL}?action=movimiento&id=${deleteTarget.id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        sessionStorage.setItem('activeTab', 'movimientos');
        window.location.reload();
      } else {
        setActionLoading(false);
        alert('Error al eliminar el movimiento');
      }
    } catch (error) {
      console.error('Error:', error);
      setActionLoading(false);
      alert('Error al eliminar el movimiento');
    }
  };

  const handleViewMovimientos = (cliente) => {
    setViewingCliente(cliente);
    setShowViewMovimientos(true);
  };

  // Pre-calcular fecha del último movimiento de cada cliente (solo una vez)
  const clientesConUltimaFecha = useMemo(() => {
    return clientes.map(cliente => {
      const movimientosCliente = movimientos.filter(m => m.clienteId === cliente.id);
      
      if (movimientosCliente.length === 0) {
        return { ...cliente, ultimaFecha: 0 };
      }
      
      const fechaMasReciente = Math.max(...movimientosCliente.map(m => {
  // PROTECCION: Si no hay fecha, retornar 0
  if (!m.fecha) return 0;
  
  try {
    // Parsear "29/11/2025 12:47" completo
    const [fechaPart, horaPart] = m.fecha.split(' ');
    const [dia, mes, anio] = fechaPart.split('/');
    const [hora, minuto] = horaPart ? horaPart.split(':') : [0, 0];
    return new Date(anio, mes - 1, dia, hora, minuto).getTime();
  } catch {
    // PROTECCION: Si algo falla, retornar 0
    return 0;
  }
}));
      
      return { ...cliente, ultimaFecha: fechaMasReciente };
    });
  }, [clientes, movimientos]);

  // Filtrar y ordenar (mucho más rápido ahora)
  const filteredClientes = useMemo(() => {
    return clientesConUltimaFecha
      .filter(cliente =>
        cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cliente.dni.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cliente.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        // Si no tienen movimientos, van al final
        if (a.ultimaFecha === 0 && b.ultimaFecha === 0) return 0;
        if (a.ultimaFecha === 0) return 1;
        if (b.ultimaFecha === 0) return -1;
        
        // Ordenar por fecha más reciente primero
        return b.ultimaFecha - a.ultimaFecha;
      });
  }, [clientesConUltimaFecha, searchTerm]);

  // RENDERS
  const renderResumen = () => {
    const totalClientes = clientes.length;
    const clientesConDeuda = clientes.filter(c => c.saldo < 0).length;
    const deudaTotal = clientes.reduce((sum, c) => c.saldo < 0 ? sum + Math.abs(c.saldo) : sum, 0);
    const saldoPositivoTotal = clientes.reduce((sum, c) => c.saldo > 0 ? sum + c.saldo : sum, 0);
    
    // Obtener últimos 25 clientes añadidos (ordenados por ID descendente)
    const ultimosClientes = [...clientes].sort((a, b) => b.id - a.id).slice(0, 25);

    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Panel General</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Total Clientes - Siempre visible */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Clientes</p>
                <p className="text-3xl font-bold text-gray-900">{totalClientes}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users size={24} className="text-[#C2185B]" />
              </div>
            </div>
          </div>

          {/* Clientes con Deuda - Oculto con botón ojo - CLICKEABLE */}
          <div 
            className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition"
            onClick={() => setShowListaDeuda(!showListaDeuda)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-sm">Clientes con Deuda</p>
                {showDeudaClientes ? (
                  <p className="text-3xl font-bold text-red-600">{clientesConDeuda}</p>
                ) : (
                  <p className="text-3xl font-bold text-gray-300">••••</p>
                )}
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="bg-red-100 p-3 rounded-full">
                  <ArrowDown size={24} className="text-red-600" />
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeudaClientes(!showDeudaClientes);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                  title={showDeudaClientes ? "Ocultar" : "Ver"}
                >
                  <Eye size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Deuda Total - Oculto con botón ojo */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-sm">Deuda Total</p>
                {showDeudaTotal ? (
                  <p className="text-3xl font-bold text-red-600">{deudaTotal.toFixed(2)}€</p>
                ) : (
                  <p className="text-3xl font-bold text-gray-300">••••€</p>
                )}
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="bg-red-100 p-3 rounded-full">
                  <DollarSign size={24} className="text-red-600" />
                </div>
                <button
                  onClick={() => setShowDeudaTotal(!showDeudaTotal)}
                  className="text-gray-500 hover:text-gray-700"
                  title={showDeudaTotal ? "Ocultar" : "Ver"}
                >
                  <Eye size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Saldo Positivo - Oculto con botón ojo */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-sm">Saldo Positivo</p>
                {showSaldoPositivo ? (
                  <p className="text-3xl font-bold text-green-600">{saldoPositivoTotal.toFixed(2)}€</p>
                ) : (
                  <p className="text-3xl font-bold text-gray-300">••••€</p>
                )}
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="bg-green-100 p-3 rounded-full">
                  <ArrowUp size={24} className="text-green-600" />
                </div>
                <button
                  onClick={() => setShowSaldoPositivo(!showSaldoPositivo)}
                  className="text-gray-500 hover:text-gray-700"
                  title={showSaldoPositivo ? "Ocultar" : "Ver"}
                >
                  <Eye size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla dinámica: Clientes con deuda o Íšltimos añadidos */}
        <div className="bg-white rounded-lg shadow p-6">
          {showListaDeuda ? (
            <>
              <h2 className="text-xl font-bold mb-4 text-red-600">Clientes con Deuda (Ordenados por Mayor Deuda)</h2>
              {(() => {
                const clientesDeuda = clientes
                  .filter(c => c.saldo < 0)
                  .sort((a, b) => a.saldo - b.saldo); // Menor saldo primero (más deuda)
                
                const itemsPerPage = 25;
                const totalPages = Math.ceil(clientesDeuda.length / itemsPerPage);
                const startIndex = (currentDeudaPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedDeuda = clientesDeuda.slice(startIndex, endIndex);

                return (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">DNI</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deuda</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedDeuda.map((cliente) => (
                            <tr key={cliente.id} className="hover:bg-gray-50">
                              <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{cliente.nombre}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">{cliente.dni}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">{cliente.email}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm font-bold text-red-600">
                                {cliente.saldo.toFixed(2)}€
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Paginación */}
                    {totalPages > 1 && (
                      <div className="mt-4 flex justify-center items-center space-x-2">
                        <button
                          onClick={() => setCurrentDeudaPage(p => Math.max(1, p - 1))}
                          disabled={currentDeudaPage === 1}
                          className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <span className="text-sm text-gray-600">
                          Página {currentDeudaPage} de {totalPages} ({clientesDeuda.length} clientes con deuda)
                        </span>
                        <button
                          onClick={() => setCurrentDeudaPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentDeudaPage === totalPages}
                          className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold mb-4">Íšltimos 25 Clientes Añadidos</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">DNI</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {ultimosClientes.map((cliente) => (
                      <tr key={cliente.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{cliente.nombre}</td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">{cliente.dni}</td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">{cliente.email}</td>
                        <td className={`px-3 py-3 whitespace-nowrap text-sm font-medium ${cliente.saldo < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {cliente.saldo.toFixed(2)}€
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderServicios = () => {
    // Ordenar servicios según el orden guardado
    const serviciosOrdenados = serviciosOrden.length > 0
      ? serviciosOrden.map(id => servicios.find(s => s.id === id)).filter(Boolean)
      : servicios;

    const handleDragStart = (e, servicioId) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('servicioId', servicioId);
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, targetId) => {
      e.preventDefault();
      const draggedId = parseInt(e.dataTransfer.getData('servicioId'));
      
      if (draggedId === targetId) return;

      const newOrden = [...serviciosOrden];
      const draggedIndex = newOrden.indexOf(draggedId);
      const targetIndex = newOrden.indexOf(targetId);

      // Intercambiar posiciones
      newOrden.splice(draggedIndex, 1);
      newOrden.splice(targetIndex, 0, draggedId);

      setServiciosOrden(newOrden);
    };

    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Servicios</h1>

        <button
          onClick={() => setShowAddServicio(true)}
          className="bg-[#C2185B] text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-[#AD1457] transition mb-6"
        >
          <Plus size={18} /> Agregar servicio
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {serviciosOrdenados.map((servicio) => (
            <div
              key={servicio.id}
              draggable
              onDragStart={(e) => handleDragStart(e, servicio.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, servicio.id)}
              className="bg-white rounded-lg shadow p-4 cursor-move hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-300"
              style={{ touchAction: 'none' }}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <div className="text-gray-400">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <circle cx="5" cy="5" r="1.5" />
                      <circle cx="5" cy="10" r="1.5" />
                      <circle cx="5" cy="15" r="1.5" />
                      <circle cx="10" cy="5" r="1.5" />
                      <circle cx="10" cy="10" r="1.5" />
                      <circle cx="10" cy="15" r="1.5" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg">{servicio.nombre}</h3>
                </div>
                <button
                  onClick={() => handleDeleteServicio(servicio.id)}
                  className="text-red-600 hover:text-red-800"
                  onMouseDown={(e) => e.stopPropagation()}
                  draggable={false}
                >
                  <Trash2 size={18} />
                </button>
              </div>
              <p className="text-2xl font-bold text-[#C2185B]">{servicio.precio.toFixed(2)}€</p>
              <p className="text-sm text-gray-500 capitalize">{servicio.tipo}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMovimientos = () => {
    const filteredMovimientos = movimientos.filter(movimiento => {
      const cliente = clientes.find(c => c.id === movimiento.clienteId);
      const servicio = servicios.find(s => s.id === movimiento.servicioId);
      const esRecarga = servicio?.nombre?.toLowerCase().includes('recarga');
      
      // Filtro de búsqueda por texto
      if (searchMovimientos) {
        const search = searchMovimientos.toLowerCase();
        const matchesSearch = (
          movimiento.fecha.toLowerCase().includes(search) ||
          cliente?.nombre.toLowerCase().includes(search) ||
          cliente?.dni.toLowerCase().includes(search) ||
          servicio?.nombre.toLowerCase().includes(search) ||
          movimiento.monto.toString().includes(search)
        );
        if (!matchesSearch) return false;
      }
      
      // Filtro por servicio
      if (filtroServicio !== 'todos' && movimiento.servicioId !== parseInt(filtroServicio)) {
        return false;
      }
      
      // Filtro por tipo (gasto/recarga)
      if (filtroTipo === 'gastos' && esRecarga) return false;
      if (filtroTipo === 'recargas' && !esRecarga) return false;
      
      // Filtro por fecha desde
      if (filtroFechaDesde) {
        const [dia, mes, año] = movimiento.fecha.split(' ')[0].split('/');
        const fechaMov = new Date(año, mes - 1, dia);
        const fechaDesde = new Date(filtroFechaDesde);
        if (fechaMov < fechaDesde) return false;
      }
      
      // Filtro por fecha hasta
      if (filtroFechaHasta) {
        const [dia, mes, año] = movimiento.fecha.split(' ')[0].split('/');
        const fechaMov = new Date(año, mes - 1, dia);
        const fechaHasta = new Date(filtroFechaHasta);
        if (fechaMov > fechaHasta) return false;
      }
      
      // Filtro por monto mínimo
      if (filtroMontoMin && movimiento.monto < parseFloat(filtroMontoMin)) {
        return false;
      }
      
      // Filtro por monto máximo
      if (filtroMontoMax && movimiento.monto > parseFloat(filtroMontoMax)) {
        return false;
      }
      
      return true;
    });
    
    const totalPages = Math.ceil(filteredMovimientos.length / movimientosPerPage);
    const startIndex = (currentMovimientosPage - 1) * movimientosPerPage;
    const endIndex = startIndex + movimientosPerPage;
    const paginatedMovimientos = filteredMovimientos
      .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
      .slice(startIndex, endIndex);

    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Movimientos</h1>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar por cliente, servicio, fecha..."
                value={searchMovimientos}
                onChange={(e) => {
                  setSearchMovimientos(e.target.value);
                  setCurrentMovimientosPage(1);
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-80"
              />
            </div>
            
            {/* Botón para mostrar/ocultar filtros avanzados */}
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter size={18} />
              Filtros avanzados
              {(filtroServicio !== 'todos' || filtroTipo !== 'todos' || filtroFechaDesde || filtroFechaHasta || filtroMontoMin || filtroMontoMax) && (
                <span className="bg-[#AD1457] text-white text-xs px-2 py-0.5 rounded-full">Activo</span>
              )}
            </button>
          </div>
          
          {/* Panel de filtros avanzados */}
          {mostrarFiltros && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Servicio</label>
                <select
                  value={filtroServicio}
                  onChange={(e) => {
                    setFiltroServicio(e.target.value);
                    setCurrentMovimientosPage(1);
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="todos">Todos los servicios</option>
                  {servicios.map(s => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={filtroTipo}
                  onChange={(e) => {
                    setFiltroTipo(e.target.value);
                    setCurrentMovimientosPage(1);
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="todos">Todos</option>
                  <option value="gastos">Solo gastos</option>
                  <option value="recargas">Solo recargas</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Desde fecha</label>
                <input
                  type="date"
                  value={filtroFechaDesde}
                  onChange={(e) => {
                    setFiltroFechaDesde(e.target.value);
                    setCurrentMovimientosPage(1);
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hasta fecha</label>
                <input
                  type="date"
                  value={filtroFechaHasta}
                  onChange={(e) => {
                    setFiltroFechaHasta(e.target.value);
                    setCurrentMovimientosPage(1);
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monto mínimo (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={filtroMontoMin}
                  onChange={(e) => {
                    setFiltroMontoMin(e.target.value);
                    setCurrentMovimientosPage(1);
                  }}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monto máximo (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={filtroMontoMax}
                  onChange={(e) => {
                    setFiltroMontoMax(e.target.value);
                    setCurrentMovimientosPage(1);
                  }}
                  placeholder="100.00"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              
              <div className="md:col-span-3 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setFiltroServicio('todos');
                    setFiltroTipo('todos');
                    setFiltroFechaDesde('');
                    setFiltroFechaHasta('');
                    setFiltroMontoMin('');
                    setFiltroMontoMax('');
                    setSearchMovimientos('');
                    setCurrentMovimientosPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Limpiar filtros
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Servicio</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Servicio</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Saldo</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedMovimientos.map((movimiento) => {
                const cliente = clientes.find(c => c.id === movimiento.clienteId);
                const servicio = servicios.find(s => s.id === movimiento.servicioId);
                const esRecarga = servicio?.nombre?.toLowerCase().includes('recarga');
                
                return (
                  <tr key={movimiento.id}>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                      {movimiento.fecha === 'Invalid Date' || !movimiento.fecha ? 'Sin fecha' : movimiento.fecha}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {cliente?.nombre || 'N/A'}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      {servicio?.nombre || 'N/A'}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      {movimiento.cantidad}
                    </td>
                    <td className={`px-3 py-3 whitespace-nowrap text-sm ${esRecarga ? 'text-green-600' : 'text-red-600'}`}>
                      {esRecarga ? '+' : '-'}{movimiento.monto.toFixed(2)}€
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium" style={{color: cliente?.saldo >= 0 ? '#10b981' : '#ef4444'}}>
                      {cliente?.saldo ? cliente.saldo.toFixed(2) : '0.00'}€
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-1">
                        <button 
                          onClick={() => handleEditMovimiento(movimiento)} 
                          className="text-[#C2185B] hover:text-[#880E4F]"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteMovimientoClick(movimiento.id)} 
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex justify-center items-center space-x-2">
            <button
              onClick={() => setCurrentMovimientosPage(p => Math.max(1, p - 1))}
              disabled={currentMovimientosPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm">
              Página {currentMovimientosPage} de {totalPages} ({movimientos.length} movimientos totales)
            </span>
            <button
              onClick={() => setCurrentMovimientosPage(p => Math.min(totalPages, p + 1))}
              disabled={currentMovimientosPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderClientes = () => {
    const totalPages = Math.ceil(filteredClientes.length / clientesPerPage);
    const startIndex = (currentClientesPage - 1) * clientesPerPage;
    const endIndex = startIndex + clientesPerPage;
    const paginatedClientes = filteredClientes.slice(startIndex, endIndex);

    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Clientes</h1>

        <div className="flex gap-4 mb-6">
          <button
            onClick={() => {
              // Buscar el servicio "Copias B/N" para preseleccionarlo
              const copiasBN = servicios.find(s => s.nombre.toLowerCase().includes('copias b/n'));
              if (copiasBN) {
                setSelectedServicio(copiasBN.id);
              }
              setShowAddGasto(true);
            }}
            className="bg-[#C2185B] text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-[#AD1457] transition"
          >
            <DollarSign size={18} /> Agregar gasto
          </button>
          <button
            onClick={() => setShowAddCliente(true)}
            className="bg-[#C2185B] text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-[#AD1457] transition"
          >
            <User size={18} /> Agregar cliente
          </button>
        </div>

        <div className="mb-4 flex gap-2">
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentClientesPage(1);
            }}
            className="border border-gray-300 rounded px-3 py-2 flex-1"
          />
          <button className="bg-gray-200 px-3 py-2 rounded flex items-center gap-1">
            <Search size={18} />
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre y Apellidos</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DNI</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Correo electrónico</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Saldo</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedClientes.map((cliente) => (
                <tr key={cliente.id}>
                  <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{cliente.nombre}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">{cliente.dni}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">{cliente.email}</td>
                  <td className={`px-3 py-3 whitespace-nowrap text-sm ${cliente.saldo < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {cliente.saldo.toFixed(2)}€
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-1">
                      <button 
                        onClick={() => handleAddGastoFromCliente(cliente.id)} 
                        className="text-[#C2185B] hover:text-[#880E4F]" 
                        title="Agregar gasto"
                      >
                        <DollarSign size={16} />
                      </button>
                      <button 
                        onClick={() => handleViewMovimientos(cliente)} 
                        className="text-green-600 hover:text-green-900" 
                        title="Ver movimientos"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        onClick={() => handleEditCliente(cliente.id)} 
                        className="text-[#C2185B] hover:text-[#880E4F]" 
                        title="Editar cliente"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        onClick={() => handleDeleteCliente(cliente.id)} 
                        className="text-red-600 hover:text-red-900" 
                        title="Eliminar cliente"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="mt-4 flex justify-center items-center space-x-2">
            <button
              onClick={() => setCurrentClientesPage(p => Math.max(1, p - 1))}
              disabled={currentClientesPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm text-gray-700">
              Página {currentClientesPage} de {totalPages} ({filteredClientes.length} clientes)
            </span>
            <button
              onClick={() => setCurrentClientesPage(p => Math.min(totalPages, p + 1))}
              disabled={currentClientesPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Lógica de renderizado principal
  if (!isAuthenticated) {
    if (isAdminRoute) {
      return <AdminLogin onLogin={handleLogin} />;
    } else {
      return <ClienteLogin onLogin={handleLogin} clientes={clientes} />;
    }
  }

  if (userType === 'cliente') {
    // Mostrar loading mientras se carga el cliente
    if (!currentCliente) {
      return <LoadingSpinner message="Cargando datos..." />;
    }
    return <ClienteView cliente={currentCliente} movimientos={movimientos} servicios={servicios} onLogout={handleLogout} />;
  }

  // Vista de administrador
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header style={{ background: 'linear-gradient(135deg, #C2185B 0%, #880E4F 100%)' }} className="text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/logo.png" 
                alt="I-Gijón" 
                className="w-12 h-12"
              />
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>I-Gijón S.A.T.</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleLogout}
                className="px-4 py-2 rounded transition flex items-center gap-2"
                style={{ background: 'rgba(255,255,255,0.2)' }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
              >
                <LogOut size={18} />
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav style={{ background: '#AD1457' }} className="text-white shadow">
        <div className="container mx-auto px-4">
          <div className="flex space-x-6">
            <button
              onClick={() => setActiveTab('resumen')}
              className={`py-3 px-4 flex items-center space-x-2 transition ${activeTab === 'resumen' ? 'bg-opacity-30 bg-black' : 'hover:bg-opacity-20 hover:bg-black'}`}
            >
              <Home size={18} />
              <span>General</span>
            </button>
            <button
              onClick={() => setActiveTab('servicios')}
              className={`py-3 px-4 flex items-center space-x-2 transition ${activeTab === 'servicios' ? 'bg-[#C2185B]' : 'hover:bg-[#FCE4EC]'}`}
            >
              <ShoppingBag size={18} />
              <span>Servicios</span>
            </button>
            <button
              onClick={() => setActiveTab('movimientos')}
              className={`py-3 px-4 flex items-center space-x-2 transition ${activeTab === 'movimientos' ? 'bg-[#C2185B]' : 'hover:bg-[#FCE4EC]'}`}
            >
              <Wallet size={18} />
              <span>Movimientos</span>
            </button>
            <button
              onClick={() => setActiveTab('clientes')}
              className={`py-3 px-4 flex items-center space-x-2 transition ${activeTab === 'clientes' ? 'bg-[#C2185B]' : 'hover:bg-[#FCE4EC]'}`}
            >
              <Users size={18} />
              <span>Clientes</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {activeTab === 'resumen' && renderResumen()}
        {activeTab === 'servicios' && renderServicios()}
        {activeTab === 'movimientos' && renderMovimientos()}
        {activeTab === 'clientes' && renderClientes()}
      </main>

      {/* Modales */}
      <AddClienteModal 
        show={showAddCliente}
        onClose={() => {
          setShowAddCliente(false);
          setEditingClienteId(null);
          setNuevoCliente({ nombre: '', email: '', dni: '', saldo: 0 });
        }}
        nuevoCliente={nuevoCliente}
        setNuevoCliente={setNuevoCliente}
        onSave={handleAddCliente}
        isEditing={editingClienteId !== null}
      />
      <AddServicioModal 
        show={showAddServicio}
        onClose={() => setShowAddServicio(false)}
        nuevoServicio={nuevoServicio}
        setNuevoServicio={setNuevoServicio}
        onSave={handleAddServicio}
      />
      <AddGastoModal 
        show={showAddGasto}
        onClose={() => {
          setShowAddGasto(false);
          setSelectedCliente(null);
          setSelectedServicio(null);
          setCantidad(1);
        }}
        clientes={clientes}
        servicios={servicios}
        selectedCliente={selectedCliente}
        setSelectedCliente={setSelectedCliente}
        selectedServicio={selectedServicio}
        setSelectedServicio={setSelectedServicio}
        cantidad={cantidad}
        setCantidad={setCantidad}
        onSave={handleAddGasto}
        clientePreseleccionado={selectedCliente ? clientes.find(c => c.id === selectedCliente) : null}
      />
      <EditMovimientoModal 
        show={showEditMovimiento}
        onClose={() => {
          setShowEditMovimiento(false);
          setEditingMovimiento(null);
        }}
        movimiento={editingMovimiento}
        clientes={clientes}
        servicios={servicios}
        onSave={handleSaveEditMovimiento}
      />
      <ViewClienteMovimientosModal 
        show={showViewMovimientos}
        onClose={() => {
          setShowViewMovimientos(false);
          setViewingCliente(null);
        }}
        cliente={viewingCliente}
        movimientos={movimientos}
        servicios={servicios}
      />
      
      {/* Modal de confirmación para borrar */}
      <ConfirmModal
        show={showConfirmDelete}
        onClose={() => {
          setShowConfirmDelete(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteMovimiento}
        title="¿Eliminar movimiento?"
        message="Esta acción no se puede deshacer. El saldo del cliente se ajustará automáticamente."
        confirmText="Eliminar"
        cancelText="Cancelar"
        danger={true}
      />
      
      {/* Loading spinner para acciones */}
      {actionLoading && <LoadingSpinner message={loadingMessage} />}
    </div>
  );
};

export default App;
